import { checkEmailExists, sendgridUnsubscribe, verifyVoucherCode } from '../util/api';
import { logVoucherDebug } from '../util/voucherHelpers';
import { getDefaultLocale } from '../util/genericHelpers';

// ================ Action types ================ //
export const VERIFY_COUPON_CODE_REQUEST = 'app/global/VERIFY_COUPON_CODE_REQUEST';
export const VERIFY_COUPON_CODE_SUCCESS = 'app/global/VERIFY_COUPON_CODE_SUCCESS';
export const VERIFY_COUPON_CODE_ERROR = 'app/global/VERIFY_COUPON_CODE_ERROR';
export const CLEAR_VERIFY_COUPON_CODE_STATES = 'app/global/CLEAR_VERIFY_COUPON_CODE_STATES';

export const CHECK_EMAIL_REQUEST = 'app/global/CHECK_EMAIL_REQUEST';
export const CHECK_EMAIL_SUCCESS = 'app/global/CHECK_EMAIL_SUCCESS';
export const CHECK_EMAIL_ERROR = 'app/global/CHECK_EMAIL_ERROR';
export const CLEAR_EMAIL_CHECK = 'app/global/CLEAR_EMAIL_CHECK';

export const SENDGRID_UNSUBSCRIBE_REQUEST = 'app/global/SENDGRID_UNSUBSCRIBE_REQUEST';
export const SENDGRID_UNSUBSCRIBE_SUCCESS = 'app/global/SENDGRID_UNSUBSCRIBE_SUCCESS';
export const SENDGRID_UNSUBSCRIBE_ERROR = 'app/global/SENDGRID_UNSUBSCRIBE_ERROR';
export const CLEAR_SENDGRID_UNSUBSCRIBE = 'app/global/CLEAR_SENDGRID_UNSUBSCRIBE';

// ================ Initial state ================ //
const initialState = {
  verifyCodeRequest: false,
  verifyCodeSuccess: false,
  verifyCodeError: false,

  isContentVoucher: false,
  couponCode: null,
  couponId: null,
  coupon: null,
  couponExpiryDate: null,
  couponExpiryTimestamp: null,
  couponExpirySource: null,

  // NEW: keep track of promotion code (when user typed a promotion code)
  promotionCodeId: null,
  promotionCode: null, // optional full object (if server returns it)

  successMessage: null,
  errorMessage: null,

  emailCheck: {
    email: null,
    checking: false,
    exists: false,
    error: null,
    checkedAt: null,
  },
  unsubscribeRequest: false,
  unsubscribeSuccess: false,
  unsubscribeError: null,
  unsubscribeMessage: null,
};

// ================ Reducer ================ //
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case VERIFY_COUPON_CODE_REQUEST:
      return {
        ...state,
        verifyCodeRequest: true,
        // clear any previous error on a new request
        errorMessage: null,
      };

    case VERIFY_COUPON_CODE_SUCCESS:
      return {
        ...state,
        verifyCodeRequest: false,
        verifyCodeSuccess: payload.isCodeValid,
        verifyCodeError: !payload.isCodeValid,

        isContentVoucher: payload.isContentVoucher,
        couponCode: payload.couponCode || null,
        couponId: payload.couponId || null,
        coupon: payload.coupon || null,
        couponExpiryDate: payload.couponExpiryDate || null,
        couponExpiryTimestamp: payload.couponExpiryTimestamp || null,
        couponExpirySource: payload.couponExpirySource || null,

        // NEW
        promotionCodeId: payload.promotionCodeId || null,
        promotionCode: payload.promotionCode || null,

        successMessage: payload.successMessage || null,
        errorMessage: payload.errorMessage || null,
      };

    case VERIFY_COUPON_CODE_ERROR:
      return {
        ...state,
        verifyCodeRequest: false,
        verifyCodeError: true,
        successMessage: null,
        errorMessage: payload || 'Error verifying voucher code',
      };

    case SENDGRID_UNSUBSCRIBE_REQUEST:
      return {
        ...state,
        unsubscribeRequest: true,
        unsubscribeSuccess: false,
        unsubscribeError: null,
        unsubscribeMessage: null,
      };

    case SENDGRID_UNSUBSCRIBE_SUCCESS:
      return {
        ...state,
        unsubscribeRequest: false,
        unsubscribeSuccess: true,
        unsubscribeError: null,
        unsubscribeMessage: payload?.message || 'Successfully unsubscribed',
      };

    case SENDGRID_UNSUBSCRIBE_ERROR:
      return {
        ...state,
        unsubscribeRequest: false,
        unsubscribeSuccess: false,
        unsubscribeError: payload || 'Failed to unsubscribe',
        unsubscribeMessage: null,
      };

    case CLEAR_SENDGRID_UNSUBSCRIBE:
      return {
        ...state,
        unsubscribeRequest: false,
        unsubscribeSuccess: false,
        unsubscribeError: null,
        unsubscribeMessage: null,
      };

    case CLEAR_VERIFY_COUPON_CODE_STATES:
      return {
        ...state,
        verifyCodeRequest: false,
        verifyCodeSuccess: false,
        verifyCodeError: false,
        isContentVoucher: false,
        couponCode: null,
        couponId: null,
        coupon: null,
        couponExpiryDate: null,
        couponExpiryTimestamp: null,
        couponExpirySource: null,

        // NEW
        promotionCodeId: null,
        promotionCode: null,

        successMessage: null,
        errorMessage: null,
      };

    case CHECK_EMAIL_REQUEST: {
      const { email } = payload || {};
      return {
        ...state,
        emailCheck: {
          email: email || null,
          checking: true,
          exists: false,
          error: null,
          checkedAt: null,
        },
      };
    }
    case CHECK_EMAIL_SUCCESS: {
      const { email, exists } = payload || {};
      return {
        ...state,
        emailCheck: {
          email: email || null,
          checking: false,
          exists: !!exists,
          error: null,
          checkedAt: Date.now(),
        },
      };
    }
    case CHECK_EMAIL_ERROR: {
      const { email, error } = payload || {};
      return {
        ...state,
        emailCheck: {
          email: email || null,
          checking: false,
          exists: false,
          error: error || 'Email check failed',
          checkedAt: Date.now(),
        },
      };
    }
    case CLEAR_EMAIL_CHECK: {
      return {
        ...state,
        emailCheck: {
          email: null,
          checking: false,
          exists: false,
          error: null,
          checkedAt: null,
        },
      };
    }

    default:
      return state;
  }
}

// ================ Action creators ================ //
export const verifyCouponCodeRequest = () => ({
  type: VERIFY_COUPON_CODE_REQUEST,
});

export const verifyCouponCodeSuccess = payload => ({
  type: VERIFY_COUPON_CODE_SUCCESS,
  payload,
});

export const verifyCouponCodeError = payload => ({
  type: VERIFY_COUPON_CODE_ERROR,
  payload,
});

export const clearCouponCodeInitialStates = payload => ({
  type: CLEAR_VERIFY_COUPON_CODE_STATES,
  payload,
});

export const checkEmailRequest = email => ({
  type: CHECK_EMAIL_REQUEST,
  payload: { email },
});

export const checkEmailSuccess = (email, exists) => ({
  type: CHECK_EMAIL_SUCCESS,
  payload: { email, exists },
});

export const checkEmailError = (email, error) => ({
  type: CHECK_EMAIL_ERROR,
  payload: { email, error },
});

export const clearEmailCheck = () => ({
  type: CLEAR_EMAIL_CHECK,
});

export const sendgridUnsubscribeRequestAction = () => ({
  type: SENDGRID_UNSUBSCRIBE_REQUEST,
});

export const sendgridUnsubscribeSuccess = message => ({
  type: SENDGRID_UNSUBSCRIBE_SUCCESS,
  payload: { message },
});

export const sendgridUnsubscribeError = error => ({
  type: SENDGRID_UNSUBSCRIBE_ERROR,
  payload: error,
});

export const clearSendgridUnsubscribe = () => ({
  type: CLEAR_SENDGRID_UNSUBSCRIBE,
});

// ================ Selectors ================ //
export const selectEmailCheck = state => state.global?.emailCheck || initialState.emailCheck;

export const selectSendgridUnsubscribe = state => {
  const g = state.global || initialState;
  return {
    inProgress: g.unsubscribeRequest,
    success: g.unsubscribeSuccess,
    error: g.unsubscribeError,
    message: g.unsubscribeMessage,
  };
};

export const verifyCodeSelector = state => {
  const g = state.global;
  return {
    inProgress: g.verifyCodeRequest,
    success: g.verifyCodeSuccess,
    error: g.verifyCodeError,

    isContentVoucher: g.isContentVoucher,
    couponCode: g.couponCode,
    couponId: g.couponId,
    coupon: g.coupon,
    couponExpiryDate: g.couponExpiryDate,
    couponExpiryTimestamp: g.couponExpiryTimestamp,
    couponExpirySource: g.couponExpirySource,
    isCodeValid: g.coupon?.valid, // Stripe coupon.valid (boolean), still handy for UI

    // NEW
    promotionCodeId: g.promotionCodeId,
    promotionCode: g.promotionCode,

    successMessage: g.successMessage,
    errorMessage: g.errorMessage,
  };
};

// ================ Thunks ================ //
/**
 * verifyCouponCode:
 * Calls /api/verify-voucher-code, which may return either a Promotion Code (promo_…)
 * or a Coupon (coupon_…). We carry both; prefer promotionCodeId when present.
 */
export const verifyCouponCode = params => async (dispatch /*, getState, sdk */) => {
  dispatch(verifyCouponCodeRequest());
  try {
    const response =
      (await verifyVoucherCode({ ...params, defaultLocale: getDefaultLocale() })) || {};

    const {
      isCodeValid = false,
      isContentVoucher = null,
      couponId = null,
      coupon = null,
      couponExpiryDate = null,
      couponExpiryTimestamp = null,
      couponExpirySource = null,
      successMessage = null,
      errorMessage = null,

      // NEW (make sure your server sets these when a promotion code is matched)
      promotionCodeId = null,
      promotionCode = null,
    } = response;

    const payload = {
      isCodeValid,
      isContentVoucher,
      couponCode: params?.code || null,
      couponId,
      coupon,
      couponExpiryDate,
      couponExpiryTimestamp,
      couponExpirySource,
      successMessage,
      errorMessage,

      // NEW
      promotionCodeId,
      promotionCode,
    };

    if (!isCodeValid || errorMessage) {
      logVoucherDebug('Verify voucher rejected', { params, response });
    } else {
      logVoucherDebug('Verify voucher response', { params, response });
    }

    dispatch(verifyCouponCodeSuccess(payload));
    return payload;
  } catch (error) {
    logVoucherDebug('Verify voucher error', { params, error: error?.message || error });
    dispatch(verifyCouponCodeError(error?.message || 'Error verifying voucher code'));
    return error;
  }
};

export const checkIfEmailExists = email => async dispatch => {
  if (!email) return;
  dispatch(checkEmailRequest(email));
  try {
    const response = (await checkEmailExists({ email })) || {};
    dispatch(checkEmailSuccess(email, !!response?.exists));
    return response;
  } catch (err) {
    dispatch(checkEmailError(email, err?.message || 'Email check failed'));
    return { exists: false };
  }
};

export const unsubscribeFromNewsletter = body => async dispatch => {
  dispatch(sendgridUnsubscribeRequestAction());

  try {
    const res = (await sendgridUnsubscribe(body)) || {};

    if (res.success) {
      dispatch(sendgridUnsubscribeSuccess(res.message || 'Successfully unsubscribed'));
    } else {
      const msg = res.error || 'Failed to process unsubscribe request. Please try again.';
      dispatch(sendgridUnsubscribeError(msg));
    }

    return res;
  } catch (err) {
    const msg = err?.message || 'Failed to process unsubscribe request. Please try again.';
    dispatch(sendgridUnsubscribeError(msg));
    return { success: false, error: msg };
  }
};
